const DAYS = {
  lunes: 1, martes: 2, miércoles: 3, miercoles: 3, jueves: 4, viernes: 5, sábado: 6, sabado: 6, domingo: 0,
  monday: 1, tuesday: 2, wednesday: 3, thursday: 4, friday: 5, saturday: 6, sunday: 0,
  montag: 1, dienstag: 2, mittwoch: 3, donnerstag: 4, freitag: 5, samstag: 6, sonntag: 0,
};

const canaryNow = () => {
  const parts = new Intl.DateTimeFormat('en-GB', {
    timeZone: 'Atlantic/Canary', weekday: 'short', hour: '2-digit', minute: '2-digit', hour12: false,
  }).formatToParts(new Date());
  const get = (t) => parts.find(p => p.type === t)?.value;
  const day = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'].indexOf(get('weekday'));
  return { day, minutes: (parseInt(get('hour'), 10) % 24) * 60 + parseInt(get('minute'), 10) };
};

const toMinutes = (str) => {
  const m = str.trim().match(/^(\d{1,2})(?::(\d{2}))?\s*(a\.?\s*m\.?|p\.?\s*m\.?)?$/i);
  if (!m) return null;
  let h = parseInt(m[1], 10);
  const min = m[2] ? parseInt(m[2], 10) : 0;
  const ap = m[3]?.toLowerCase().replace(/[.\s]/g, '');
  if (ap === 'pm' && h < 12) h += 12;
  if (ap === 'am' && h === 12) h = 0;
  return h * 60 + min;
};

const fmt = (mins) => {
  const m = ((mins % 1440) + 1440) % 1440;
  return `${String(Math.floor(m / 60)).padStart(2, '0')}:${String(m % 60).padStart(2, '0')}`;
};

// Parses Google weekday_text lines into {day: [[start,end], ...]} (minutes; end may exceed 1440 when crossing midnight)
const parseHours = (lines) => {
  const byDay = {};
  for (const line of lines || []) {
    const idx = line.indexOf(':');
    if (idx < 0) continue;
    const day = DAYS[line.slice(0, idx).trim().toLowerCase()];
    if (day === undefined) continue;
    const rest = line.slice(idx + 1).trim();
    if (/24 h|24 horas|24 hours|24 stunden|open 24/i.test(rest)) { byDay[day] = 'all'; continue; }
    if (/cerrado|closed|geschlossen/i.test(rest)) { byDay[day] = []; continue; }
    const ranges = [];
    for (const part of rest.split(',')) {
      const [a, b] = part.split(/\s*[–—-]\s*/);
      if (!a || !b) continue;
      const s = toMinutes(a.replace(/\u202f|\u00a0/g, ' '));
      let e = toMinutes(b.replace(/\u202f|\u00a0/g, ' '));
      if (s === null || e === null) continue;
      if (e <= s) e += 1440;
      ranges.push([s, e]);
    }
    byDay[day] = ranges;
  }
  return byDay;
};

export const getOpenStatus = (openingHours) => {
  if (!openingHours || openingHours.length === 0) return { state: 'unknown' };
  const byDay = parseHours(openingHours);
  if (Object.keys(byDay).length === 0) return { state: 'unknown' };
  const { day, minutes } = canaryNow();
  const today = byDay[day];
  const yesterday = byDay[(day + 6) % 7];

  if (today === 'all') return { state: 'open24' };
  // Range from yesterday spilling past midnight
  if (Array.isArray(yesterday)) {
    for (const [s, e] of yesterday) {
      if (e > 1440 && minutes + 1440 >= s && minutes + 1440 < e) {
        return { state: 'open', closesAt: fmt(e), closingSoon: e - (minutes + 1440) <= 60 };
      }
    }
  }
  if (Array.isArray(today)) {
    for (const [s, e] of today) {
      if (minutes >= s && minutes < e) return { state: 'open', closesAt: fmt(e), closingSoon: e - minutes <= 60 };
    }
    const next = today.find(([s]) => s > minutes);
    if (next) return { state: 'closed', opensAt: fmt(next[0]) };
  }
  // Find next opening in following days
  for (let i = 1; i <= 7; i++) {
    const d = byDay[(day + i) % 7];
    if (d === 'all') return { state: 'closed', opensAt: '00:00', opensInDays: i };
    if (Array.isArray(d) && d.length) return { state: 'closed', opensAt: fmt(d[0][0]), opensInDays: i };
  }
  return { state: 'closed' };
};

export const openStatusLabel = (status, lang = 'es') => {
  const T = {
    es: { open24: 'Abierto 24 h', open: 'Abierto', closes: 'cierra a las', soon: 'Cierra pronto', closed: 'Cerrado', opens: 'abre a las', tomorrow: 'abre mañana a las' },
    en: { open24: 'Open 24h', open: 'Open', closes: 'closes at', soon: 'Closing soon', closed: 'Closed', opens: 'opens at', tomorrow: 'opens tomorrow at' },
    de: { open24: 'Durchgehend geöffnet', open: 'Geöffnet', closes: 'schließt um', soon: 'Schließt bald', closed: 'Geschlossen', opens: 'öffnet um', tomorrow: 'öffnet morgen um' },
  }[lang] || {};
  if (status.state === 'open24') return { main: T.open24, detail: '' };
  if (status.state === 'open') return { main: status.closingSoon ? T.soon : T.open, detail: status.closesAt ? `${T.closes} ${status.closesAt}` : '' };
  if (status.state === 'closed') {
    if (!status.opensAt) return { main: T.closed, detail: '' };
    const when = status.opensInDays === 1 ? T.tomorrow : status.opensInDays > 1 ? T.opens : T.opens;
    return { main: T.closed, detail: `${when} ${status.opensAt}` };
  }
  return null;
};
